import { parse } from 'papaparse'
import { readCSVFile } from '@/models/readCSVFileUtil'
import BulkAddLocationFormModel from './BulkAddLocationForm'
import { makeTemporaryId } from '@/models/Simulation'
import { uniq, uniqWith } from 'lodash'
import _isEqual from 'lodash/isEqual'
import { extractImportableAndErrorLocations } from '@/models/Location/ExtractImportableAndErrorLocationsForLocationGroupAndLocationBulk'

export default class BulkAddLocationGroupAndLocationFormModel {
  constructor({ geocoder, radius }) {
    this.createdLocationGroups = []
    this.geocoder = geocoder
    this.radius = radius
    this.csv = ''
    this.errorLocationCsv = ''
  }

  get parseAndDistinctLocations() {
    // csvから取得し、重複を排除
    const parseLocations = uniqWith(parseCSV(this.csv), _isEqual)
    // 何も入力されていない行を排除してから返す
    return parseLocations.filter(location => {
      if (
        (location.locationGroupName === undefined ||
          location.locationGroupName === '') &&
        (location.locationName === undefined || location.locationName === '') &&
        (location.address === undefined || location.address === '')
      )
        return false
      return true
    })
  }

  async readCSVFile(file) {
    const text = await readCSVFile(file)
    this.errorLocationCsv = ''
    // 3カラムのみ読み込み、かつ何も入力されてない場合空文字にreplace
    this.csv = parse(text)
      .data.map(([locationGroupName, locationName, address]) => {
        const validLocationGroupName = (() => {
          if (locationGroupName === undefined) return ''
          return locationGroupName
        })()
        const validLocationName = (() => {
          if (locationName === undefined) return ''
          return locationName
        })()
        const validAddress = (() => {
          if (address === undefined) return ''
          return address
        })()
        return `${validLocationGroupName},${validLocationName},${validAddress}`
      })
      .join(`\n`)
      .trim()
      .replace('undefined', '')
  }

  async save() {
    const parsedLocations = this.parseAndDistinctLocations
    const geocoder = this.geocoder
    const locations = await extractImportableAndErrorLocations({
      parsedLocations,
      geocoder,
    })
    // 入稿できない地点がある場合、errorを返す
    if (locations.errorLocations.length > 0) {
      const error = new Error(
        `上記の${locations.errorLocations.length}地点が正しいフォーマットではないもしくは、住所を座標へ変換できないため登録できません。\n上記の地点（エラー地点）を登録する場合、情報を修正のうえ下記の入力欄（入稿地点一覧）へ追記のうえ再度登録ボタンを押してください。\n上記の地点を登録しない場合、そのまま再度登録ボタンを押してください。`,
      )
      error.locations = locations
      throw error
    }
    const importableLocations = locations.importableLocations
    const locationGroupsStr = Array.from(
      uniq(
        importableLocations.map(usableLocation => {
          return usableLocation.locationGroupName
        }),
      ),
    ).join('\n')
    const errorLocations = []

    // グループ名を一括登録
    const parsedLocationGroups = locationGroupsStr
      .trim()
      .split(/\n/)
      .filter(name => name.trim().length > 0)
    try {
      this.createdLocationGroups = await Promise.all(
        parsedLocationGroups.map((name, index) => {
          return {
            id: makeTemporaryId(index),
            name,
            accessibleType: 'public', // 固定
            simulationLocations: [],
            bulkAddLocationForm: null,
            added: true,
            radius: this.radius,
          }
        }),
      )
    } catch (e) {
      // エラーが発生した"グループ名,地点名,住所"をリストに詰める
      e.errors.forEach(error => {
        const location = importableLocations
          .filter(location => {
            return error.locationGroup.name === location.locationGroupName
          })
          .map(location => {
            return {
              locationGroupName: error.locationGroup.name,
              locationName: location.locationName,
              address: location.address,
            }
          })
        errorLocations.push(location)
      })
    }

    // 地点登録用フォームリスト作成
    this.createdLocationGroups.forEach(createdLocationGroup => {
      createdLocationGroup.bulkAddLocationForm = new BulkAddLocationFormModel({
        locationGroupName: createdLocationGroup.name,
        geocoder: this.geocoder,
      })

      createdLocationGroup.bulkAddLocationForm.csv = importableLocations
        .filter(location => {
          return location.locationGroupName === createdLocationGroup.name
        })
        .map(location => location.locationName + ',' + location.address)
        .join('\n')
    })

    // 地点登録
    const locationErrorsList = []
    await Promise.all(
      this.createdLocationGroups.map(async createdLocationGroup => {
        try {
          createdLocationGroup.simulationLocations = await createdLocationGroup.bulkAddLocationForm.createLocations()
        } catch (e) {
          locationErrorsList.push(e)
        }
      }),
    )

    // エラー処理
    // 登録時に発生したエラー"グループ名,地点名,住所"をリストに詰める
    if (locationErrorsList.length > 0) {
      const uploadError = new Error(
        '上記の' +
          locations.locationErrorsList.length +
          '一部登録できなかった地点があります。\n上記の地点以外は登録が完了しています。\n恐れ入りますが、登録が完了しているグループへ上記の地点を別途登録してください。',
      )
      locationErrorsList.forEach(errors => {
        errors.errors.forEach(error => {
          const location = {
            locationGroupName: error.locationGroupName,
            locationName: error.location.name,
            address: error.location.address,
          }
          errorLocations.push(location)
        })
      })
      uploadError.locations = {
        importableLocations: [],
        errorLocations: errorLocations,
      }
      throw uploadError
    }

    return [this.createdLocationGroups, errorLocations]
  }
}

/**
 * csvからグループ名、地点名、住所を取り出す
 * @param {string} csv
 * @return {{ name: string, address: string }[]}
 */
const parseCSV = csv => {
  const result = parse(csv)
  return result.data.map(([locationGroupName, locationName, address]) => ({
    locationGroupName,
    locationName,
    address,
  }))
}
